// Typed API client (openapi-fetch over the generated OpenAPI types).
//
// Session model (ADR-0013): the BFF session lives in an HttpOnly cookie the
// browser sends with `credentials: "include"`; JavaScript never sees it. The
// CSRF token is kept ONLY in memory (never storage) and sent on every unsafe
// request. Responses are normalized into ApiProblem. A 403 STEP_UP_REQUIRED
// asks the user to confirm their second factor and retries once.

import createClient from "openapi-fetch";
import { API_ORIGIN } from "./config";
import { ApiProblem, OFFLINE_ERROR, problemFrom } from "./errors";
import type { paths } from "./schema.gen";

let csrfToken: string | null = null;

export const csrf = {
  get: () => csrfToken,
  set: (value: string | null) => {
    csrfToken = value;
  },
};

const UNSAFE = new Set(["POST", "PUT", "PATCH", "DELETE"]);

export const api = createClient<paths>({
  baseUrl: API_ORIGIN ?? "",
  credentials: "include",
  // Resolved per request (not captured at import) so tests and adapters can substitute it.
  fetch: (request: Request) => globalThis.fetch(request),
});

api.use({
  onRequest({ request }) {
    if (UNSAFE.has(request.method) && csrfToken) request.headers.set("X-CSRF-Token", csrfToken);
    return request;
  },
});

// ---------------------------------------------------------------- step-up coordination

type StepUpHandler = () => Promise<boolean>;
let stepUpHandler: StepUpHandler | null = null;

/** Registered by the StepUpDialog; resolves true when the user confirmed their second factor. */
export function registerStepUpHandler(handler: StepUpHandler | null) {
  stepUpHandler = handler;
}

// ---------------------------------------------------------------- calls

interface Result<T> {
  data?: T;
  error?: unknown;
  response: Response;
}

/**
 * Runs a typed call, returns its data or throws ApiProblem. On 403
 * STEP_UP_REQUIRED it asks for step-up once and retries the same call.
 */
export async function call<T>(run: () => Promise<Result<T>>, retried = false): Promise<T> {
  let result: Result<T>;
  try {
    result = await run();
  } catch {
    throw new ApiProblem(0, "NETWORK", OFFLINE_ERROR);
  }
  if (result.response.ok) return result.data as T;
  const problem = problemFrom(result.response.status, result.error);
  problem.requestId = result.response.headers.get("X-Request-Id");
  if (problem.code === "STEP_UP_REQUIRED" && !retried && stepUpHandler && (await stepUpHandler())) {
    return call(run, true);
  }
  throw problem;
}
